import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaMinus, FaDownload, FaSearch, FaTimes } from 'react-icons/fa';
import '../assets/css/ProductPage.css';
import Breadcrumbs from '../components/Breadcrumbs';
import { API_BASE_URL, UPLOADS_BASE_URL } from '../config';

// Import desk images
import desk1 from '../assets/images/products/desks/1.png';
import desk2 from '../assets/images/products/desks/2.png';
import desk3 from '../assets/images/products/desks/3.png';
import desk4 from '../assets/images/products/desks/4.png';
import desk5 from '../assets/images/products/desks/5.png';
import desk6 from '../assets/images/products/desks/6.png';

// Import PDF files
import productCatalogue from '../assets/images/pdf/product-catalogue.pdf';
import companyProfile from '../assets/images/pdf/company-profile.pdf';

const ExecutiveDeskPage = ({ category }) => {
  const { subcategory } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Responsive state management
  const [screenWidth, setScreenWidth] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  
  // Existing state
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [products, setProducts] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageOrientations, setImageOrientations] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  
  // Get stored pagination state or default to 1
  const getInitialPage = () => {
    // Check URL params first
    const urlParams = new URLSearchParams(location.search);
    const pageFromUrl = urlParams.get('page');
    if (pageFromUrl && !isNaN(pageFromUrl)) {
      return parseInt(pageFromUrl);
    }
    
    // Check sessionStorage for this specific subcategory
    const storageKey = `pagination-${category}-${subcategory}`;
    const storedPage = sessionStorage.getItem(storageKey);
    if (storedPage && !isNaN(storedPage)) {
      return parseInt(storedPage);
    }
    
    return 1;
  };
  
  // Pagination state with preserved state
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [productsPerPage] = useState(15);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState(() => {
    // Check if there's a stored search term for this subcategory
    const storageKey = `search-${category}-${subcategory}`;
    const storedSearch = sessionStorage.getItem(storageKey);
    return storedSearch || '';
  });
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // New state for mobile dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Current selected category for display
  const currentCategory = subcategories.find(subcat => 
    subcategory === subcat.name.toLowerCase().replace(/\s+/g, '-')
  );

  // State for grid layout based on image orientations
  const [gridClass, setGridClass] = useState('mixed-grid');

  // Function to determine grid class based on image orientations
  const determineGridClass = (orientations, products) => {
    if (Object.keys(orientations).length === 0) return 'mixed-grid';
    
    const landscapeCount = Object.values(orientations).filter(isLandscape => isLandscape).length;
    const portraitCount = Object.values(orientations).filter(isLandscape => !isLandscape).length;
    const total = landscapeCount + portraitCount;
    
    if (total === 0) return 'mixed-grid';
    
    const landscapePercentage = (landscapeCount / total) * 100;
    
    // If 80% or more are landscape, use landscape grid (3 columns)
    if (landscapePercentage >= 80) {
      return 'landscape-grid';
    }
    // If 80% or more are portrait, use portrait grid (4 columns)
    else if (landscapePercentage <= 20) {
      return 'portrait-grid';
    }
    // Mixed orientations, use default (3 columns)
    else {
      return 'mixed-grid';
    }
  };

  // Handle responsive behavior
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setScreenWidth(width);
    setIsMobile(width <= 767);
    setIsTablet(width >= 768 && width <= 991);
    setIsDesktop(width > 991);
  }, []);

  useEffect(() => {
    handleResize();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [handleResize]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isDropdownOpen) {
        const dropdown = event.target.closest('.category-dropdown');
        if (!dropdown) {
          setIsDropdownOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobile, isDropdownOpen]);

  const handleOrientationChange = () => {
    setTimeout(handleResize, 100);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e) => {
    if (!isMobile) return;
    try {
      if (e.currentTarget && e.currentTarget.classList) {
        e.currentTarget.classList.add('touching');
      }
    } catch (error) {
      // Silently handle any touch event errors
      console.warn('Touch start error:', error);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isMobile) return;
    try {
      if (e.currentTarget && e.currentTarget.classList) {
        setTimeout(() => {
          if (e.currentTarget && e.currentTarget.classList) {
            e.currentTarget.classList.remove('touching');
          }
        }, 100);
      }
    } catch (error) {
      // Silently handle any touch event errors
      console.warn('Touch end error:', error);
    }
  };
  
  // Get category ID based on category name
  const getCategoryId = (categoryName) => {
    switch(categoryName) {
      case 'Office Furniture':
        return 1;
      case 'Office Chairs':
        return 2;
      case 'Storage Solutions':
        return 3;
      case 'Sofa in Lounge':
        return 4;
      case 'Acoustic Solutions':
        return 5;
      case 'Wall Cladding':
        return 6;
      default:
        return 1;
    }
  };
  
  // Get the correct route prefix for each category
  const getCategoryRoute = (categoryName) => {
    switch(categoryName) {
      case 'Office Furniture':
        return 'office-furniture';
      case 'Office Chairs':
        return 'office-chairs';
      case 'Storage Solutions':
        return 'storage-solutions';
      case 'Sofa in Lounge':
        return 'sofa-lounges';
      case 'Acoustic Solutions':
        return 'acoustic-solutions';
      case 'Wall Cladding':
        return 'wall-cladding';
      default:
        return 'office-furniture';
    }
  };
  
  // Define breadcrumb links
  const breadcrumbLinks = [
    { url: '/', text: 'Home' },
    { url: `/${getCategoryRoute(category)}`, text: category },
    { url: `/${getCategoryRoute(category)}/${subcategory}`, text: subcategory?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }
  ];
  
  // Fetch subcategories for the current category
  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        const categoryId = getCategoryId(category);
        const response = await fetch(`${API_BASE_URL}subcategories.php?category_id=${categoryId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch subcategories');
        }
        const data = await response.json();
        if (data.success && Array.isArray(data.subcategories)) {
          setSubcategories(data.subcategories);
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error);
      }
    };

    fetchSubcategories();
  }, [category]);

  // Check image orientation function
  const checkImageOrientation = (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function() {
        const isLandscape = this.width > this.height;
        resolve(isLandscape);
      };
      img.onerror = function() {
        resolve(true); // Default to landscape on error
      };
      img.src = imageUrl;
    });
  };

  // Update useEffect for products to check orientations and set grid class
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const formattedSubcategory = subcategory
          ?.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        const response = await fetch(`${API_BASE_URL}products.php?subcategory=${formattedSubcategory}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          // Sort products alphabetically by name
          const sortedProducts = data.sort((a, b) => a.name.localeCompare(b.name));
          setProducts(sortedProducts);
          // Check orientation for each product image
          const orientations = {};
          await Promise.all(
            sortedProducts.map(async (product) => {
              if (product.images?.[0]) {
                const imageUrl = `${UPLOADS_BASE_URL}products/${product.images[0]}`;
                const isLandscape = await checkImageOrientation(imageUrl);
                orientations[product.id] = isLandscape;
              }
            })
          );
          setImageOrientations(orientations);
          
          // Determine and set the appropriate grid class
          const newGridClass = determineGridClass(orientations, sortedProducts);
          setGridClass(newGridClass);
        } else if (data.products && Array.isArray(data.products)) {
          // Sort products alphabetically by name
          const sortedProducts = data.products.sort((a, b) => a.name.localeCompare(b.name));
          setProducts(sortedProducts);
        } else {
          setProducts([]);
        }
        setRetryCount(0);
      } catch (error) {
        console.error('Error fetching products:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (subcategory) {
      fetchProducts();
    }
  }, [subcategory, retryCount]);

  // Get responsive categories display count
  const getCategoriesDisplayCount = () => {
    if (isMobile) return 3;
    if (isTablet) return 4;
    return 5;
  };

  const displayCount = getCategoriesDisplayCount();
  const visibleCategories = showAllCategories ? subcategories : subcategories.slice(0, displayCount);
  
  const toggleCategories = () => {
    setShowAllCategories(!showAllCategories);
  };
  
  // Toggle dropdown for mobile
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDownload = (pdfFile, fileName) => {
    const link = document.createElement('a');
    link.href = pdfFile;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // Search handlers
  const handleSearchChange = (e) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    
    // Save search term to sessionStorage
    const storageKey = `search-${category}-${subcategory}`;
    if (newSearchTerm.trim()) {
      sessionStorage.setItem(storageKey, newSearchTerm);
    } else {
      sessionStorage.removeItem(storageKey);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    // Clear from sessionStorage
    const storageKey = `search-${category}-${subcategory}`;
    sessionStorage.removeItem(storageKey);
  };

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Reset to page 1 only when products change due to new subcategory, not when returning from product detail
  useEffect(() => {
    // Only reset if this is a new subcategory (not returning from product detail)
    const urlParams = new URLSearchParams(location.search);
    const pageFromUrl = urlParams.get('page');
    
    if (!pageFromUrl) {
      setCurrentPage(1);
    }
  }, [products, location.search]);

  // Effect to restore pagination state when subcategory changes
  useEffect(() => {
    if (subcategory) {
      const newPage = getInitialPage();
      setCurrentPage(newPage);
      
      // Update URL if needed
      const urlParams = new URLSearchParams(location.search);
      const currentUrlPage = urlParams.get('page');
      
      if (newPage > 1 && (!currentUrlPage || parseInt(currentUrlPage) !== newPage)) {
        urlParams.set('page', newPage.toString());
        const newUrl = `${location.pathname}?${urlParams.toString()}`;
        navigate(newUrl, { replace: true });
      } else if (newPage === 1 && currentUrlPage) {
        urlParams.delete('page');
        const newUrl = `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        navigate(newUrl, { replace: true });
      }
    }
  }, [subcategory, category]);

  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
    // Only reset to first page when search changes, not when component mounts with existing search
    const urlParams = new URLSearchParams(location.search);
    const pageFromUrl = urlParams.get('page');
    if (!pageFromUrl) {
      setCurrentPage(1);
    }
  }, [products, searchTerm, location.search]);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    
    // Save to sessionStorage for this specific subcategory
    const storageKey = `pagination-${category}-${subcategory}`;
    sessionStorage.setItem(storageKey, pageNumber.toString());
    
    // Update URL with page parameter
    const urlParams = new URLSearchParams(location.search);
    if (pageNumber > 1) {
      urlParams.set('page', pageNumber.toString());
    } else {
      urlParams.delete('page');
    }
    
    const newUrl = `${location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
    navigate(newUrl, { replace: true });
    
    // Scroll to top of products grid
    const productsGrid = document.querySelector('.products-grid');
    if (productsGrid) {
      productsGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const handleImageError = (e) => {
    e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
  };

  if (loading) {
    return (
      <div className={`loading-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}`}>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`error-container ${isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}`}>
        <div className="error-content">
          <h3>Error Loading Products</h3>
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`product-page ${isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'}`}>
      {/* Breadcrumbs Section */}
      <Breadcrumbs 
        title={subcategory?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} 
        links={breadcrumbLinks} 
      />

      {/* Product Content Section */}
      <div className="container-fluid px-4">
        <div className="product-content">
        
          <div className="row">
            {/* Sidebar */}
            <div className={`categories-sidebar col-md-4 ${isMobile ? 'mobile-sidebar' : ''}`}>
              <h3 className="categories-title">
                {isMobile ? category.split(' ')[0] : category} Categories
              </h3>

              {isMobile ? (
                // Mobile Dropdown
                <div className="category-dropdown">
                  <button 
                    className="dropdown-toggle"
                    onClick={toggleDropdown}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <span className="dropdown-current">
                      {currentCategory ? (
                        currentCategory.name.length > 20 
                          ? `${currentCategory.name.substring(0, 20)}...`
                          : currentCategory.name
                      ) : 'Select Category'}
                    </span>
                    <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                  
                  <ul className={`category-list dropdown-list ${isDropdownOpen ? 'open' : ''}`}>
                    {subcategories.map((subcat) => (
                      <li key={subcat.id} className="category-item">
                        <Link
                          to={`/${getCategoryRoute(category)}/${subcat.name.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`category-link ${subcategory === subcat.name.toLowerCase().replace(/\s+/g, '-') ? 'active' : ''}`}
                          onClick={() => setIsDropdownOpen(false)}
                          onTouchStart={handleTouchStart}
                          onTouchEnd={handleTouchEnd}
                        >
                          {subcat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                // Desktop Categories List
                <ul className="category-list">
                  {visibleCategories.map((subcat) => (
                    <li key={subcat.id} className="category-item">
                      <Link
                        to={`/${getCategoryRoute(category)}/${subcat.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`category-link ${subcategory === subcat.name.toLowerCase().replace(/\s+/g, '-') ? 'active' : ''}`}
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                      >
                        {subcat.name}
                      </Link>
                    </li>
                  ))}
                  {subcategories.length > displayCount && (
                    <li className="category-item">
                      <button 
                        onClick={toggleCategories} 
                        className="download-btn show-more-btn"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                      >
                        {showAllCategories ? (
                          <>
                            <FaMinus/>
                            Show Less
                          </>
                        ) : (
                          <>
                            <FaPlus/>
                            Show More ({subcategories.length - displayCount})
                          </>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              )}

              {/* PDF Downloads Section */}
              <div className="downloads-section">
                <h4 className="downloads-title">Downloads</h4>
                <div className="download-buttons">
                  <button 
                    onClick={() => handleDownload(productCatalogue, 'Product-Catalogue.pdf')}
                    className="download-btn"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <FaDownload className="download-icon" />
                    Product Catalogue
                  </button>
                  <button 
                    onClick={() => handleDownload(companyProfile, 'Company-Profile.pdf')}
                    className="download-btn"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                  >
                    <FaDownload className="download-icon" />
                    Company Profile
                  </button>
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className={`main-content-area col-md-8 ${isMobile ? 'mobile-content' : ''}`}>
              {/* Search Bar Section */}
              <div className="search-section">
                <div className="search-container">
                  <div className="search-input-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search products by name..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                    {searchTerm && (
                      <button
                        onClick={clearSearch}
                        className="clear-search-icon"
                        aria-label="Clear search"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <div className="search-results-info">
                      Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} for "{searchTerm}"
                    </div>
                  )}
                </div>
              </div>

              {/* Products Grid */}
              <div className={`products-grid ${gridClass} ${isMobile ? 'mobile-grid' : isTablet ? 'tablet-grid' : 'desktop-grid'}`}>
                {filteredProducts.length === 0 ? (
                  <div className="no-products">
                    <div className="no-products-content">
                      {searchTerm ? (
                        <>
                          <h3>No Products Found</h3>
                          <p>No products match your search "{searchTerm}"</p>
                          <p>Try adjusting your search terms or browse other categories.</p>
                          <button onClick={clearSearch} className="clear-search-btn">
                            Clear Search
                          </button>
                        </>
                      ) : (
                        <>
                          <h3>No Products Found</h3>
                          <p>We don't have any products in this category yet.</p>
                          <p>Please check back later or browse other categories.</p>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  currentProducts.map((product) => (
                    <div 
                      key={product.id} 
                      className="product-card"
                    >
                      <Link to={`/product/${product.id}?page=${currentPage}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ''}`}>
                        <div className={`product-image-container ${imageOrientations[product.id] ? 'landscape' : 'portrait'}`}>
                          <img
                            src={`${UPLOADS_BASE_URL}products/${product.images[0]}`}
                            alt={product.name}
                            className="product-image"
                            onError={handleImageError}
                            loading="lazy"
                          />
                        </div>
                        <div className="product-info-main">
                          <h3 className="product-name">{product.name}</h3>
                        </div>
                      </Link>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination Controls */}
              {filteredProducts.length > productsPerPage && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>
                      Showing {indexOfFirstProduct + 1} to {Math.min(indexOfLastProduct, filteredProducts.length)} of {filteredProducts.length} products
                    </span>
                  </div>
                  
                  <nav className="pagination-nav" aria-label="Product pagination">
                    <ul className="pagination">
                      {/* Previous Button */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          aria-label="Previous page"
                        >
                          Previous
                        </button>
                      </li>

                      {/* Page Numbers */}
                      {getPageNumbers().map((pageNumber, index) => (
                        <li 
                          key={index} 
                          className={`page-item ${pageNumber === currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                        >
                          {pageNumber === '...' ? (
                            <span className="page-link">...</span>
                          ) : (
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNumber)}
                              aria-label={`Go to page ${pageNumber}`}
                              aria-current={pageNumber === currentPage ? 'page' : undefined}
                            >
                              {pageNumber}
                            </button>
                          )}
                        </li>
                      ))}

                      {/* Next Button */}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          aria-label="Next page"
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDeskPage;